import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

const linkLesson0117 = getLinkForCourseLesson(
  "41545ab2-1f99-40ea-8225-21d5e62ab7b0",
  "1b088e13-8675-4efa-a3d6-8add2f52944c"
);

const theory = `In this lesson, in order to find the solutions of the wave equation in a cylindrical waveguide, we first search for solutions in cylindrical coordinates. We then present the general formalism of the modal approach of waveguides in general.
<h3 class=sectionHead>Solutions in cylindrical coordinates</h3>
<p>In cylindrical coordinates the wave equation can be written
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi mathvariant=italic>Δp</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>z</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>r</mi>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>∂p</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂r</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂r</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>𝜃</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   In the frequency domain, setting
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>R</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mi>Θ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mi>Z</mi>
      <mo class=MathClass-open>(</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, leads to
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>Rr</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂r</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Θ</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Θ</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>𝜃</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>Z</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   The sum of terms of the left-hand side is independent of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and so
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>Z</mi>
         </mrow>
      </mfrac>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mi>z</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>Θ</mi>
         </mrow>
      </mfrac>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>Θ</mi>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mi>𝜃</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>
   have to be constant. This leads to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Θ</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>m𝜃</mi>
         </mrow>
      </msup>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
   </math>
   a relative integer because (at least in most situations) the solution has to verify
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Θ</mi>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>Θ</mi>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>±</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>.
<p>Finally, given these results, it remains
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mi>r</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂r</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <msup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <msubsup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-close>)</mo>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <mi>m</mi>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   This equation is a Bessel equation whose solutions are the Bessel functions of the variable
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mi>r</mi>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <msup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>−</mo>
      <msubsup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-close>)</mo>
   </math>. The solutions basis becomes
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>z</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>J</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jm𝜃</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                  </msub>
                  <mi>z</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   are the Bessel functions, respectively, of first and second kind, the second is sometimes called Neumann function and is also often noted
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   . We note that Neumann function diverges at zero. It is also noted that Bessel functions are similar to functions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
   </math>
   in the far-field with a decrease in the amplitude as a function of the square root of the distance since for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mi>r</mi>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
      <mn>1</mn>
   </math>
   we have
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>J</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                     <mrow>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                        </msub>
                        <mi>r</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>r</mi>
            <mo class=MathClass-bin>−</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mn>2</mn>
            <mi>m</mi>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>π</mi>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mn>4</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
            </math>
            <td class=eq-no>(5)
            <tr>
            <td style="font-size: 13px">
            <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>r</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                     <mrow>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>k</mi>
                           </mrow>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                        </msub>
                        <mi>r</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>r</mi>
            <mo class=MathClass-bin>−</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mn>2</mn>
            <mi>m</mi>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>π</mi>
            <mo class=MathClass-bin stretchy=false>∕</mo>
            <mn>4</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   These solutions are stationary in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>. On can equally well choose a solutions basis progressive in the far field, setting
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   (Hankel functions of the first and second species).
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
   </math>
   corresponds to divergent cylindrical waves and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
   </math>
   to converging cylindrical waves. The solution basis is then
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msup>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>z</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msubsup>
            <msubsup>
               <mrow>
                  <mi>H</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jm𝜃</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                  </msub>
                  <mi>z</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
   </math>
   a relative integer.
<h3 class=sectionHead>Cylindrical waveguide</h3>
<p>
   Here we consider a guide with perfectly reflecting walls with a circular cross section of radius
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>
   . Because the pressure at the center must have a finite amplitude, amplitudes
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   related to Neumann functions must be zero. The solutions compatible with the boundary conditions are
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>z</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>n</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </munder>
            <msubsup>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>J</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
                  <mo class=MathClass-punc>;</mo>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
            </msub>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jm𝜃</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                        <mo class=MathClass-punc>;</mo>
                        <mi mathvariant=italic>mn</mi>
                     </mrow>
                  </msub>
                  <mi>z</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>∂p</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂r</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   i.e.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mi>R</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   is the first derivative of the Bessel function of order
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
   </math>
   which has an infinite number of solutions indexed
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
   </math>. There is therefore a discrete set of possible values for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mi>R</mi>
   </math>. This set of values is tabulated in the literature. We give in <a href="#" data-img-src=${config.ASSETS_URL}/01/19/theory/table-1.svg data-img-alt="Table 1. First values
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mi>R</mi>
   </math>
   of the zeros of Bessel functions.">Table 1</a> a few of them.
<div class=table>
   <figure class=float>
      <figcaption class=caption>
         Table 1. First values
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
                  <mo class=MathClass-punc>;</mo>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
            </msub>
            <mi>R</mi>
         </math>
         of the zeros of Bessel functions.
      </figcaption>
      <img src=${config.ASSETS_URL}/01/19/theory/table-1.svg alt="Table 1. First values
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>k</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
               <mo class=MathClass-punc>;</mo>
               <mi mathvariant=italic>mn</mi>
            </mrow>
         </msub>
         <mi>R</mi>
      </math>
      of the zeros of Bessel functions.">
   </figure>
</div>
<p>
   As for the rectangular waveguide (see <a href="${linkLesson0117}" target="_blank">lesson 1.17</a>), plane mode (0,0) is always propagative and modes
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mi>m</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>n</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are evanescent when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
   </math>
   since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msubsup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
                  <mo class=MathClass-punc>;</mo>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </mrow>
      </msqrt>
   </math>. Notice that the basis
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Ψ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mi>r</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jm𝜃</mi>
         </mrow>
      </msup>
   </math>
   is a basis of helical modes i.e. progressive modes in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>. If the problem is symmetrical, it is often more appropriate to use a stationary modal basis in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   i.e.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Ψ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mi>r</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-open>(</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
      <mo class=MathClass-rel>∈</mo>
      <mi>ℕ</mi>
   </math>. These modes present on the section
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
   </math>
   diametrical nodal lines and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
   </math>
   circular nodal lines. Some examples are given in <a href="#" data-img-src=${config.ASSETS_URL}/01/19/theory/figure-1.svg data-img-alt="Figure 1. Nodal lines in a cylindrical waveguide for modes <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mo class=MathClass-open>(</mo><mi>m</mi><mo class=MathClass-punc>,</mo><mi>n</mi><mo class=MathClass-close>)</mo><mo class=MathClass-rel>=</mo><mo class=MathClass-open>(</mo><mn>0</mn><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>1</mn><mo class=MathClass-open>[</mo><mi class=qopname>sin</mi><mo>⁡</mo><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>1</mn><mo class=MathClass-open>[</mo><mi class=qopname>cos</mi><mo>⁡</mo><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>2</mn><mo class=MathClass-open>[</mo><mi class=qopname>sin</mi><mo>⁡</mo><mn>2</mn><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>0</mn><mo class=MathClass-punc>,</mo><mn>1</mn><mo class=MathClass-close>)</mo></math>.">Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/19/theory/figure-1.svg alt="Figure 1. Nodal lines in a cylindrical waveguide for modes <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mo class=MathClass-open>(</mo><mi>m</mi><mo class=MathClass-punc>,</mo><mi>n</mi><mo class=MathClass-close>)</mo><mo class=MathClass-rel>=</mo><mo class=MathClass-open>(</mo><mn>0</mn><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>1</mn><mo class=MathClass-open>[</mo><mi class=qopname>sin</mi><mo>⁡</mo><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>1</mn><mo class=MathClass-open>[</mo><mi class=qopname>cos</mi><mo>⁡</mo><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>2</mn><mo class=MathClass-open>[</mo><mi class=qopname>sin</mi><mo>⁡</mo><mn>2</mn><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>0</mn><mo class=MathClass-punc>,</mo><mn>1</mn><mo class=MathClass-close>)</mo></math>."><figcaption class=caption>Figure 1. Nodal lines in a cylindrical waveguide for modes <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mo class=MathClass-open>(</mo><mi>m</mi><mo class=MathClass-punc>,</mo><mi>n</mi><mo class=MathClass-close>)</mo><mo class=MathClass-rel>=</mo><mo class=MathClass-open>(</mo><mn>0</mn><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>1</mn><mo class=MathClass-open>[</mo><mi class=qopname>sin</mi><mo>⁡</mo><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>1</mn><mo class=MathClass-open>[</mo><mi class=qopname>cos</mi><mo>⁡</mo><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>2</mn><mo class=MathClass-open>[</mo><mi class=qopname>sin</mi><mo>⁡</mo><mn>2</mn><mi>𝜃</mi><mo class=MathClass-close>]</mo><mo class=MathClass-punc>,</mo><mn>0</mn><mo class=MathClass-close>)</mo><mo class=MathClass-open>(</mo><mn>0</mn><mo class=MathClass-punc>,</mo><mn>1</mn><mo class=MathClass-close>)</mo></math>.</figcaption></figure>
<h3 class=sectionHead>Waveguide of any shape and modal formalism</h3>
<p>Consider a waveguide of which the cross section has an arbitrary shape. In any case the solutions of the wave equation pressure compatible with the boundary conditions can be written
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close stretchy=false>)</mo>
         <mo class=MathClass-rel>=</mo>
         <munder class=msub>
            <mrow>
               <mo>∑</mo>
            </mrow>
            <mrow>
               <mi>m</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>n</mi>
               <mo class=MathClass-punc>,</mo>
               <mo class=MathClass-bin>±</mo>
            </mrow>
         </munder>
         <msubsup>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>mn</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>±</mo>
            </mrow>
         </msubsup>
         <msub>
            <mrow>
               <mi>Ψ</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>mn</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open stretchy=false>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>y</mi>
         <mo class=MathClass-close stretchy=false>)</mo>
         <msup>
            <mrow>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>±</mo>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>k</mi>
                  </mrow>
                  <mrow>
                     <mi>z</mi>
                     <mo class=MathClass-punc>;</mo>
                     <mi mathvariant=italic>mn</mi>
                  </mrow>
               </msub>
               <mi>z</mi>
            </mrow>
         </msup>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are the modes of the cross section and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
   </math>
   their amplitude. These functions are
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mo class=MathClass-punc>;</mo>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jm𝜃</mi>
         </mrow>
      </msup>
   </math>
   for the cylindrical wave guide and for the rectangular wave guide
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nπx</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>mπy</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
   </math>. An important property of the modes is their orthogonality which allows to build a basis. In other word any field on the cross section can be written as a linear combination of the modes and this linear combination is unique. The orthogonality writes
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Ψ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Ψ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>pq</mi>
               </mrow>
            </msub>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>S</mi>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>Λ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>δ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mp</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>δ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>nq</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>
   is the cross section and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>δ</mi>
   </math>
   the Kronecker symbol which is
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
   if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>p</mi>
   </math>
   (or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>q</mi>
   </math>) and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
   </math>
   if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
      <mo class=MathClass-rel>≠</mo>
      <mi>p</mi>
   </math>
   (or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
      <mo class=MathClass-rel>≠</mo>
      <mi>q</mi>
   </math>).
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Λ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
   </math>
   is the norm of the mode. As an example for the rectangular waveguide it is
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Λ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
   </math>. When defining an orthonormal basis, it is convenient to use normalised modes by dividing the modes by their norm:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mover class=mml-overline accent=false>
               <mrow>
                  <mi>Ψ</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>Ψ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Λ</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. In the case of the rectangular waveguide the normalized modes are
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Ψ</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nπx</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>mπy</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
   </math>
   The amplitude associated to each mode is
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msubsup>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mi>S</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>±</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>y</mi>
            <mo class=MathClass-punc>,</mo>
            <mn>0</mn>
            <mo class=MathClass-close>)</mo>
            <msub>
               <mrow>
                  <mover class=mml-overline accent=false>
                     <mrow>
                        <mi>Ψ</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>mn</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>y</mi>
            <mo class=MathClass-close>)</mo>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>S</mi>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   Notice that the ingoing and outgoing waves have to be separated beforehand so that the mode amplitudes can be determined. An important point is that all modes except plane mode (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>n</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>) have a cutoff frequency. This implies that in practice, under the cutoff frequency, their amplitude
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>mn</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>|</mo>
   </math>
   are very small and negligible far from the sources. Under the first cutoff frequency evanescent mode are only present near the source in the region where the wave is not plane. They are needed to describe the domain where the wave is not plane.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>M. Abramowitz and I.A. Stegun. Handbook of mathematical functions. Dover, 1965.
   <dt>[2]
   <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
   <dt>[3]
   <dd>M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
   <dt>[4]
   <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
   <dt>[5]
   <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
   <dt>[6]
   <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
   <dt>[7]
   <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
